import { createRoot } from "react-dom/client";
import App from "./App";
import AccessGate from "./AccessGate";
import ErrorBoundary from "./ErrorBoundary";
import { Toaster } from "./components/ui/sonner";
import { SK_PRIVACY_ICON } from './constants/storageKeys';
import "./index.css";

// Apply saved privacy icon on initial load (before React renders)
try {
  const PRIVACY_ICON_CONFIGS: Record<string, { manifest: string; touchIcon: string; themeColor: string; name: string }> = {
    notes: { manifest: '/manifest-notes.json', touchIcon: '/icons/icon-notes-192.svg', themeColor: 'var(--color-text-secondary)', name: 'Notes' },
    journal: { manifest: '/manifest-journal.json', touchIcon: '/icons/icon-journal-192.svg', themeColor: 'var(--color-text-secondary)', name: 'Journal' },
  };
  const savedIconId = localStorage.getItem(SK_PRIVACY_ICON);
  if (savedIconId && PRIVACY_ICON_CONFIGS[savedIconId]) {
    const cfg = PRIVACY_ICON_CONFIGS[savedIconId];
    const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (manifestLink) manifestLink.href = cfg.manifest;
    const touchIconLink = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    if (touchIconLink) touchIconLink.href = cfg.touchIcon;
    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (themeMeta) themeMeta.content = cfg.themeColor;
    const titleMeta = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    if (titleMeta) titleMeta.content = cfg.name;
    document.title = cfg.name;
  }
} catch { /* error handled silently */ }

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <AccessGate>
      <App />
      <Toaster position="bottom-center" />
    </AccessGate>
  </ErrorBoundary>
);

// BUG-008 fix: use build-time stamp instead of hardcoded version string
// so each deploy automatically busts stale service worker caches.
const SW_CACHE_NAME = `careweave-${import.meta.env.VITE_BUILD_ID || Date.now()}`;

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  navigator.serviceWorker
    .register('/sw.js')
    .then(async () => {
      await navigator.serviceWorker.ready;

      if (!('caches' in window)) return;

      const cache = await caches.open(SW_CACHE_NAME);
      const origin = window.location.origin;

      const urlsToCache = performance
        .getEntriesByType('resource')
        .map((entry) => (entry as PerformanceResourceTiming).name)
        .filter((url) => url.startsWith(origin) && !url.includes('/api/'));

      urlsToCache.push(origin + '/');

      await Promise.allSettled(urlsToCache.map((url) => cache.add(url).catch(() => { /* error handled silently */ })));
    })
    .catch(() => { /* error handled silently */ });
}
